import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as queryString from "query-string";

import { Button, Icon, Loader, Menu, Message, Segment } from "semantic-ui-react";

import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import AnalysisBoxWhiskerChart from "../../Widgets/Charts/analysisBoxWhiskerChart";
import CorrelationMatrix from "../../Widgets/CorrelationMatrix";
import SummaryTable from "../../Widgets/SummaryTable";
import Timecourse from "../../Widgets/Timecourse";

import { useQuery } from "@tanstack/react-query";
import {
	fetchPlotAnalysisStatusForTrial,
	fetchSummaryTableDataOptions,
	// fetchTrialAnalysisDataOptions,
} from "../../../../apis/trialApi.ts";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import { TrialAnalysisProvider, useTrialAnalysisDispatch } from "./TrialAnalysisContext";

import _ from "lodash";
import PropType from "prop-types";
import { buildSummaryTableSelectionsFromPreset, getOrderedTimeSeriesPresets } from "./utils";
import { DateTime } from "luxon";

import styles from "./styles.module.css";
import Tooltip from "rc-tooltip";
import { useTimeout } from "../../../../hooks/useTimeout";

const summaryTable = "summary-table";
const boxWhiskerChart = "box-whisker";
const timecourse = "timecourse";
const correlationMatrix = "correlation-matrix";

const TrialAnalysis = () => {
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const location = useLocation();
	const history = useHistory();
	const entityNames = useSelector((state) => (state.entityNames ? state.entityNames : null));
	const { getTokenSilently } = useAuth0();

	const [navOpen, setNavOpen] = useState(true);

	const urlQueryStrings = queryString.parse(location.search);
	const qsAt = urlQueryStrings.at;

	const getAnalysisTypeFromQueryString = useCallback(() => {
		if (
			!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisTimecourse, userAuth.currentClientId) ||
			!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisSummaryTable, userAuth.currentClientId)
		) {
			return boxWhiskerChart;
		} else {
			switch (qsAt) {
				case summaryTable:
					return summaryTable;
				case boxWhiskerChart:
					return boxWhiskerChart;
				case correlationMatrix:
					return correlationMatrix;
				default:
					return timecourse;
			}
		}
	}, [qsAt, userAuth]);

	//-- Data
	const trialName =
		entityNames.trialId && moduleNavigation.trialId?.toUpperCase() === entityNames.trialId?.toUpperCase()
			? `${entityNames.trialName} (${entityNames.cooperatorName}, ${entityNames.city}, ${entityNames.region}, ${entityNames.countryCode})`
			: null;
	//-- UI Control
	const isLoaded =
		entityNames.trialId && moduleNavigation.trialId?.toUpperCase() === entityNames.trialId?.toUpperCase();
	// const [correlationChartEnabled] = useState(false);

	const activeItem = userAuth.isReady
		? getAnalysisTypeFromQueryString()
		: userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisSummaryTable, userAuth.currentClientId)
			? summaryTable
			: boxWhiskerChart;

	const { data: isDataDone } = useQuery({
		queryKey: ["plotAnalysisStatus", moduleNavigation.trialId, userAuth.currentClientId],
		queryFn: async () =>
			fetchPlotAnalysisStatusForTrial(moduleNavigation.trialId, userAuth.currentClientId, await getTokenSilently()),
		staleTime: Infinity,
		select: (data) => data.data,
	});

	useTimeout(
		() => {
			setNavOpen(false);
		},
		navOpen ? 15000 : null,
	);

	return !isLoaded || !userAuth.isReady ? (
		<Loader active />
	) : isLoaded && !trialName && userAuth.isReady ? (
		<Segment style={{ marginLeft: 50, marginTop: 15 }}>
			<p>{"We could not find analysis data for this trial."}</p>
		</Segment>
	) : (
		<Segment basic style={{ paddingTop: 0, height: "100%" }}>
			<Menu vertical icon className={`${styles.trialAnalysisNav} ${navOpen ? styles.open : ""}`}>
				<Tooltip
					placement="right"
					align={{ offset: [-3, 0] }}
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					overlay={<p>Timecourse</p>}
				>
					<Menu.Item
						name="Timecourse"
						icon="graph line increase"
						active={activeItem === timecourse}
						onClick={() => {
							history.push(`?at=${timecourse}`);
							setNavOpen(false);
						}}
						disabled={!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisTimecourse, userAuth.currentClientId)}
					>
						<Icon size="large" className="graph line increase" />
					</Menu.Item>
				</Tooltip>
				<Tooltip
					placement="right"
					align={{ offset: [-3, 0] }}
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					overlay={<p>Box Whisker</p>}
				>
					<Menu.Item
						name="Box Whisker"
						active={activeItem === boxWhiskerChart}
						onClick={() => {
							history.push(`?at=${boxWhiskerChart}`);
							setNavOpen(false);
						}}
						disabled={!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisBoxWhisker, userAuth.currentClientId)}
					>
						<Icon size="large" className="box-whisker" />
					</Menu.Item>
				</Tooltip>
				<Tooltip
					placement="right"
					align={{ offset: [-3, 0] }}
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					overlay={<p>Summary Table</p>}
				>
					<Menu.Item
						name="Summary Table"
						active={activeItem === summaryTable}
						onClick={() => {
							history.push(`?at=${summaryTable}`);
							setNavOpen(false);
						}}
						disabled={!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisSummaryTable, userAuth.currentClientId)}
					>
						<Icon size="large" className="table" />
					</Menu.Item>
				</Tooltip>
				<Tooltip
					placement="right"
					align={{ offset: [-3, 0] }}
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					overlay={<p>Correlation Matrix</p>}
				>
					<Menu.Item
						name="Correlation Matrix"
						active={activeItem === correlationMatrix}
						onClick={() => {
							history.push(`?at=${correlationMatrix}`);
							setNavOpen(false);
						}}
						disabled={
							!userAuth.hasApplicationArea(ApplicationArea.TrialAnalysisCorrelationMatrix, userAuth.currentClientId)
						}
					>
						<Icon size="large" className="grid" />
					</Menu.Item>
				</Tooltip>
				<Button
					className={styles.navMenuToggle}
					onClick={() => {
						setNavOpen((prev) => !prev);
					}}
					icon
				>
					<Icon className={`arrow ${navOpen ? "left" : "right"}`} />
				</Button>
			</Menu>
			{!isDataDone && (
				<Message warning>
					Plot analysis data for this trial is currently being recalculated, so data may not be complete or up to date.
					Please refresh the page in a few minutes.
				</Message>
			)}
			<TrialAnalysisProvider>
				<TrialAnalysisPagesWithContext activeItem={activeItem} />
			</TrialAnalysisProvider>
			{activeItem === correlationMatrix ? <CorrelationMatrix /> : null}
		</Segment>
	);
};

const TrialAnalysisPagesWithContext = ({ activeItem }) => {
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const { getTokenSilently } = useAuth0();

	const { initializeSettings, changeDisplaySettings } = useTrialAnalysisDispatch();

	const { data: trialOptions } = useQuery({
		queryKey: ["trialAnalysisOptions", moduleNavigation.trialId],
		queryFn: async () =>
			fetchSummaryTableDataOptions(moduleNavigation.trialId, userAuth.currentClientId, await getTokenSilently()),
		staleTime: Infinity,
	});

	useEffect(() => {
		if (trialOptions) {
			const timeSeriesPresets = getOrderedTimeSeriesPresets(trialOptions);

			const defaultPreset = _.maxBy(timeSeriesPresets, "instanceCount");
			const defaultQrTypeId =
				_.find(trialOptions.quantifiedRegionTypeOptions, (r) => {
					return r.isDefaultQuantifiedRegion === true;
				})?.id ??
				_.find(trialOptions.quantifiedRegionTypeOptions, (q) => {
					return q.name === "Plot-Centered with Subsamples"; // FIXME: make into a constant in utils
				})?.id ??
				trialOptions.quantifiedRegionTypeOptions[0]?.id;

			const defaultExp = trialOptions.experimentalDesignFactors.length
				? (_.find(trialOptions.experimentalDesignFactors, (ed) => ed.default) ??
					trialOptions.experimentalDesignFactors[0])
				: undefined;

			const firstFlight = _.sortBy(trialOptions.trialAnalysisOptions, (tao) => DateTime.fromISO(tao.flightDate))[0]
				.flightId;

			const defaultSummaryPreset =
				_.find(trialOptions.summaryTablePresets, "default") ?? trialOptions.summaryTablePresets[0];

			const summaryTableSelections = buildSummaryTableSelectionsFromPreset(trialOptions, defaultSummaryPreset, true);

			const fallbackMeanComparisonId = _.find(
				trialOptions.meanComparisonOptions,
				(mco) => mco.name === "Tukey HSD",
			)?.id;
			const fallbackAlphaOptionId = _.find(trialOptions.alphaOptions, (ao) => ao.value === 0.1)?.id;

			initializeSettings({
				assessmentId: defaultPreset.id,
				meanComparisonOptionId: trialOptions.defaultMeanComparisonId ?? fallbackMeanComparisonId ?? null,
				alphaOptionId: trialOptions.defaultAlphaValueId ?? fallbackAlphaOptionId ?? null,
				analysisTypeId: defaultPreset.analysisTypeId,
				quantifiedRegionTypeId: defaultQrTypeId,
				orthoImageTypeId: defaultPreset.orthoImageTypeId,
				analysisId: defaultPreset.analysisId,
				flightId: firstFlight,

				summaryTablePresetId: defaultSummaryPreset?.id,
				summaryTableSelections: summaryTableSelections,
			});

			changeDisplaySettings({
				experimentalDesignId: defaultExp?.id,
				treatmentNames: defaultExp?.treatmentNames,
				isRCBD: (defaultExp?.factorType ?? "RCBD") === "RCBD",
			});
		}
	}, [initializeSettings, changeDisplaySettings, trialOptions]);

	return (
		<>
			{activeItem === timecourse ? <Timecourse trialOptions={trialOptions} /> : null}
			{activeItem === boxWhiskerChart ? <AnalysisBoxWhiskerChart trialOptions={trialOptions} /> : null}
			{activeItem === summaryTable ? <SummaryTable rawTrialOptions={trialOptions} /> : null}
		</>
	);
};

TrialAnalysisPagesWithContext.propTypes = {
	activeItem: PropType.string,
};

export default TrialAnalysis;
